import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthenticationService } from '../security/services/authentication.service';
import { ClinicTypeEnum } from '../enums/ClinicTypeEnum';
import { generateUUID } from '../shared/utilities';
import { UserCache } from './user-cache';
import { SignalRService } from './signalr-service';
import { PetAlertKind, PetContextSnapshot, PetSource } from './pet-context.models';

interface LocalContext {
  patientKey: number; // Memory only. NEVER serialize this type.
  contextKey: string;
  ready: boolean;
  kinds: PetAlertKind[];
  birthday: boolean;
  fingerprint: string;
}

@Injectable({ providedIn: 'root' })
export class PetContextService implements OnDestroy {
  readonly connected$ = new BehaviorSubject<boolean>(false);
  private readonly publisher = generateUUID();
  private readonly sources = new Map<PetSource, LocalContext>();
  private readonly emptyKey = generateUUID();
  private revision = 0;
  private lastFingerprint = '';
  private current: PetContextSnapshot;
  private suspended = false;
  private generation = 0;
  private debounce: ReturnType<typeof setTimeout>;
  private heartbeat: ReturnType<typeof setInterval>;
  private readonly logout: Subscription;

  constructor(private signal: SignalRService, auth: AuthenticationService, private zone: NgZone) {
    this.logout = auth.logoutEvent.subscribe(() => { void this.disconnect(); this.sources.clear(); });
  }

  private get supported(): boolean {
    const type = UserCache.getLoginUser()?.Clinic?.Type;
    return type === ClinicTypeEnum.DentistClinic || type === ClinicTypeEnum.WesternAndDentistClinic;
  }

  begin(source: PetSource, patientKey: number): void {
    if (!this.supported) return;
    const previous = this.sources.get(source);
    if (previous && previous.patientKey === patientKey) return;
    this.sources.set(source, { patientKey: patientKey || 0, contextKey: generateUUID(), ready: false,
      kinds: [], birthday: false, fingerprint: '' });
    this.schedule();
  }

  update(source: PetSource, patientKey: number, kinds: PetAlertKind[], birthday: boolean, fingerprint: string): void {
    if (!this.supported) return;
    this.begin(source, patientKey);
    const context = this.sources.get(source);
    Object.assign(context, { ready: !!patientKey, kinds: kinds.slice().sort(), birthday, fingerprint });
    this.schedule();
  }

  release(source: PetSource): void { this.sources.delete(source); this.schedule(); }

  setSuspended(value: boolean): void {
    if (this.suspended === value) return;
    this.suspended = value;
    // Screen lock clears desktop context immediately.
    this.flush();
  }

  private schedule(): void {
    clearTimeout(this.debounce);
    this.zone.runOutsideAngular(() => { this.debounce = setTimeout(() => this.flush(), 300); });
  }

  private flush(): void {
    const source: PetSource = this.sources.has('register-dentistry') ? 'register-dentistry' : 'patient-info-window';
    const local = this.suspended ? null : this.sources.get(source);
    const ready = !!local?.ready;
    const fingerprint = JSON.stringify([source, local?.contextKey, ready, ready && local.fingerprint,
      ready && local.kinds, ready && local.birthday]);
    if (fingerprint === this.lastFingerprint) return;
    this.lastFingerprint = fingerprint;
    this.current = {
      Version: 1, ContextKey: local?.contextKey || this.emptyKey, Revision: ++this.revision,
      Source: source, HasPatient: ready, AlertKinds: ready ? local.kinds.slice() : [],
      BirthdayNearby: ready && local.birthday
    };
    void this.publish();
  }

  async pair(): Promise<{ text: string; expiresAt: string }> {
    if (!this.supported || this.suspended) throw new Error('Pet unavailable');
    await this.disconnect();
    const generation = this.generation;
    const pairing = await this.signal.createPetPairing(this.publisher);
    if (generation !== this.generation) {
      await this.signal.revokePetPairing(this.publisher);
      throw new Error('Pairing cancelled');
    }
    this.connected$.next(true);
    this.flush();
    await this.publish();
    if (generation !== this.generation || !this.connected$.value) throw new Error('Pairing cancelled');
    this.zone.runOutsideAngular(() => { this.heartbeat = setInterval(() => void this.publish(), 15000); });
    return { text: JSON.stringify({ Version: 1, HubUrl: this.signal.petReceiverUrl, Ticket: pairing.Ticket }),
      expiresAt: pairing.ExpiresAt };
  }

  private async publish(): Promise<void> {
    if (!this.connected$.value || !this.current) return;
    const generation = this.generation;
    try {
      const active = await this.signal.publishPetContext(this.publisher, this.current);
      if (!active && generation === this.generation) this.zone.run(() => { void this.disconnect(); });
    } catch (_) {
      // Existing singleton SignalR owns reconnect. Retry the SAME revision on the heartbeat.
      // A disconnected desktop clears its context after 45 seconds; no clinical flow is blocked.
    }
  }

  async disconnect(): Promise<void> {
    ++this.generation;
    clearInterval(this.heartbeat);
    const wasConnected = this.connected$.value;
    this.connected$.next(false);
    if (wasConnected) {
      try { await this.signal.revokePetPairing(this.publisher); } catch (_) { /* capability/stale TTL still applies */ }
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.debounce);
    this.logout.unsubscribe();
    void this.disconnect();
    this.connected$.complete();
  }
}
